/**
 * Loads the active investment-range dropdown options from Supabase.
 * The form populates its <select> from this — when the admin edits
 * `investment_ranges` rows in Supabase, the form picks up changes
 * on next mount with no redeploy.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client';
import type { InvestmentRangeRow } from '../lib/supabase/types';

export type RangeOption = Pick<
  InvestmentRangeRow,
  'slug' | 'display_label' | 'display_order'
>;

export type RangesState =
  | { status: 'loading' }
  | { status: 'ok'; ranges: RangeOption[] }
  | { status: 'error'; message: string };

export function useInvestmentRanges(): RangesState {
  const [state, setState] = useState<RangesState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('investment_ranges')
      .select('slug, display_label, display_order')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setState({ status: 'error', message: error.message });
        } else {
          setState({
            status: 'ok',
            ranges: (data ?? []) as RangeOption[],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
